// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The front door (M3-37, 08 §1).
//
// "The landing route's only job is to get a stranger's hands on the plunger before they decide to
// leave." So it is the machine, full bleed, in attract mode, with one primary control — and
// nothing else. No hero copy above the fold, no feature grid, no cookie wall, no modal, and **no
// account**: an anonymous player creates no Supabase Auth identity and no guest token, because
// creating an auth user in the shared project fires three other applications' triggers.
//
// The temptation this route exists to resist is explaining the product. A stranger who has to
// read a paragraph to find out that it is pinball has already been told less than one second of
// attract mode tells them.
//
// AND IT IS THE ONE ROUTE WITH AN ACTIVE MACHINE (M9-32)
// ------------------------------------------------------
// **A stranger's front door is unchanged by everything below.** `useActiveMachine` answers
// `flagship` for a browser that has never opened the Builder, `ready` is true on the first render
// for that browser, and `switchable` is false — so there is no extra control, no extra wait and no
// extra byte fetched. All of it is for the author, who until now had a draft that lived in React
// state, died on a reload, and could only be felt inside a framed canvas in a scrolling editor.
//
// **`key` remounts the stage.** `MachineStage` builds its runtime, its history and its whole
// machine at mount and does not depend on the definition afterwards — deliberately, because a
// machine that changed under a ball would be neither version. Switching machines is therefore not
// a prop change, it is a new stage, and saying so with a key is the whole of it.
//
// `/play/:machineId` has no active machine and must not: `machineSource.ts` has said since M3-37
// why a link that quietly became something else would give the person who receives it a different
// game.

import { useEffect, useMemo } from 'react'
import machineJson from '../../../machines/neon-nova/machine.json?raw'
import { CabinetSkeleton, LazyMachineStage } from '../components/LazyMachineStage'
import { ActiveMachineSwitch } from '../components/ActiveMachineSwitch'
import { activeMachineFor } from '../activeMachine'
import { useActiveMachine } from '../useActiveMachine'
import { clearPageMeta, setPageMeta } from '../pageMeta'
import { PAGE_COPY, machineCopy } from '../../seo/pageCopy'

export function Landing(): React.JSX.Element {
  const { choice, draft, ready } = useActiveMachine()
  // The same bytes `MachineStage` would have parsed for itself; parsed here because the decision
  // about which machine to run is this route's, and `activeMachineFor` is deliberately told what
  // the default is rather than knowing.
  const flagship = useMemo(() => JSON.parse(machineJson) as unknown, [])
  const active = activeMachineFor(choice, draft, flagship)
  const playing = titleOf(active.definition)

  // The heading is screen-reader only and says what the *page* is; the machine says its own
  // name on `/play/{machine}`. Read from `pageCopy` so the prerendered front door and this one
  // cannot disagree about it (S2-02, S3-04).
  // `?? ''` rather than an optional pass-through, because `exactOptionalPropertyTypes` is on and
  // "the property is absent" and "the property is undefined" are different types here. An empty
  // string is never reached: `PAGE_COPY['/']` is asserted to exist by `test/seo/pageCopy.test.ts`.
  const heading = PAGE_COPY['/']?.h1 ?? ''

  /**
   * Name the draft in the shell, so the page does not say two things at once (M9-32).
   *
   * The top bar renders `page?.h1 ?? route.title`, and `route.title.play` is the literal string
   * `NEON NOVA` — right for a front door that is always the flagship, and a second machine name on
   * screen the moment it is not. Measured at 1280 x 820: a top bar reading NEON NOVA over a machine
   * the switch beneath it called STARFALL, which is precisely the confusion one active machine
   * exists to remove.
   *
   * **Only for the draft**, and only at runtime. The prerendered front door and its SEO title are
   * `PAGE_COPY['/']` and stay that way: this machine is in one browser, is not on the roster, and is
   * nobody else's front door. `setPageMeta` is the mechanism `PlayRoute` already names a machine
   * with, address and all, so a stale page cannot title the next one.
   */
  const draftName = active.choice === 'draft' ? playing : ''
  useEffect(() => {
    if (draftName === '') return undefined
    const copy = machineCopy({ title: draftName })
    setPageMeta('/', { title: copy.title, description: copy.description, h1: copy.h1 })
    return () => {
      clearPageMeta('/')
    }
  }, [draftName])

  // **Only a browser that chose the draft waits**, and it waits for its own machine rather than
  // being shown somebody else's first. Everyone else — every stranger, and every author who
  // switched back — is `ready` on the first render and never sees this.
  if (!ready) {
    // The skeleton `PlayRoute` already uses, in the box it already needs: `.skeleton` is absolutely
    // positioned, so without `page__skeleton` round it, it fills the shell rather than the pane.
    return <CabinetSkeleton />
  }

  return (
    <LazyMachineStage
      key={active.choice}
      attract
      heading={heading}
      machine={active.definition}
      unscored={!active.scored}
      {...(active.switchable
        ? {
            switcher: (
              <ActiveMachineSwitch
                choice={active.choice}
                playing={playing}
                draftTitle={active.draftTitle}
              />
            ),
          }
        : {})}
    />
  )
}

/** What a machine calls itself, falling back to its id — the rule `MachineStage` already uses. */
function titleOf(definition: unknown): string {
  const meta = (definition as { meta?: { title?: unknown } }).meta
  const title = typeof meta?.title === 'string' ? meta.title.trim() : ''
  if (title !== '') return title
  const id = (definition as { machineId?: unknown }).machineId
  return typeof id === 'string' ? id : ''
}
